import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import SEO from '../components/SEO';
import { ArrowLeft, Clock, Loader2, ChevronDown, Layers } from 'lucide-react';
import { getCategoryColor } from '../constants/categoryColors';
import { getArticleSnippet } from '../utils/articleHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 24;

function CategoryPage() {
  const { slug } = useParams();
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const color = getCategoryColor(slug);
  const displayName = color.name;

  useEffect(() => {
    window.scrollTo(0, 0);
    setSkip(0);
    setArticles([]);
    fetchInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API}/articles`, { params: { category: slug, skip: 0, limit: PAGE_SIZE } }),
        axios.get(`${API}/articles/count`, { params: { category: slug } }),
      ]);
      setArticles(listRes.data);
      setTotal(countRes.data.count);
      setSkip(listRes.data.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await axios.get(`${API}/articles`, {
        params: { category: slug, skip, limit: PAGE_SIZE },
      });
      setArticles((prev) => [...prev, ...data]);
      setSkip((prev) => prev + data.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = articles.length < total;

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="category-page">
      <SEO
        title={`${displayName} — All Stories`}
        description={`Browse all ${total || ''} ${displayName} crime news articles on Crime Newz.`}
        path={`/category/${slug}`}
        type="website"
      />
      <Header />

      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <AdSlot slot="top" height={90} />
      </div>

      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="mb-6">
          <Link
            to="/"
            data-testid="category-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] hover:text-[#E63946] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Home
          </Link>
        </div>

        <div
          className="mb-10 pb-6 border-b-4 flex flex-wrap items-end justify-between gap-4"
          style={{ borderColor: color.bg }}
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Layers size={24} style={{ color: color.bg }} />
              <span
                className="text-xs tracking-[0.2em] uppercase font-bold px-3 py-1"
                style={{ backgroundColor: color.bg, color: color.text }}
              >
                CATEGORY
              </span>
            </div>
            <h1
              data-testid="category-page-title"
              className="text-4xl sm:text-5xl tracking-tight leading-none font-black text-[#1A1A2E]"
            >
              {displayName.toUpperCase()}
            </h1>
            {!loading && (
              <p className="text-sm text-[#525252] mt-3" data-testid="category-page-count">
                {total} {total === 1 ? 'article' : 'articles'} in this category
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={32} className="animate-spin" style={{ color: color.bg }} />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-24" data-testid="category-empty-state">
            <Layers size={48} className="text-[#D5D5D5] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#1A1A2E] mb-2">
              No articles in {displayName} yet
            </h2>
            <p className="text-[#525252] mb-6">Check back soon or browse other categories.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e]"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        ) : (
          <>
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="category-articles-grid"
            >
              {articles.map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  data-testid={`category-article-${idx}`}
                  className="group bg-white border border-[#E5E5E5] hover:border-[#E63946] overflow-hidden transition-colors flex flex-col"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-[0.2em] uppercase mb-2 w-fit"
                      style={{ backgroundColor: color.tint, color: color.bg }}
                    >
                      {displayName}
                    </span>
                    <h3 className="text-base font-bold text-[#1A1A2E] leading-snug group-hover:text-[#E63946] transition-colors mb-2 flex-1">
                      {article.title}
                    </h3>
                    <p className="text-xs text-[#525252] mb-3 line-clamp-2">
                      {getArticleSnippet(article.content, 90)}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs text-[#8A8A8A] mt-auto">
                      <Clock size={12} />
                      {new Date(article.published_at).toLocaleDateString()}
                      <span>•</span>
                      <span>{article.author}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-10">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="category-load-more"
                  style={{ borderColor: color.bg, color: color.bg }}
                  className="flex items-center gap-2 px-8 py-3 border-2 font-bold text-sm tracking-[0.2em] uppercase transition-colors disabled:opacity-50 hover:text-white"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = color.bg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {loadingMore ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      LOADING...
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} />
                      LOAD MORE
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <div className="max-w-[1600px] mx-auto px-6 pb-6">
        <AdSlot slot="bottom" height={90} />
      </div>

      <Footer />
    </div>
  );
}

export default CategoryPage;
